import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, Signal } from '@angular/core';

import { PRODUCT_INFO } from '@hsc/common/constants/product-info';
import { MaintenanceWidget } from '@components/maintenance-widget/maintenance-widget';
import { QuotingFlow } from '@components/quoting-flow/quoting-flow';
import { SampleContractModal } from '@components/sample-contract.modal';
import { COVERED_ITEMS } from '@constants/covered-items';
import { FullStoryEventDirective } from '@directives/fullstory-event.directive';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { scrollToSection } from '@utilities/scroll-to-section';

@Component({
  selector: 'hsc-hero-widget',
  imports: [CommonModule, QuotingFlow, MaintenanceWidget, SampleContractModal, FullStoryEventDirective],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  template: `
    <div class="bolt-container">
      <div class="flex flex-row items-start gap-6 py-8">
        <div class="flex flex-[2] flex-shrink-0 flex-col items-center justify-center gap-0 py-6">
          <div class="flex flex-col items-center justify-center gap-2.5">
            <h2 class="text-vibrant-blue bolt-heading-lg text-center text-nowrap">
              <span class="flex">
                <div class="bolt-heading-lg bolt-serif">Nationwide</div>
                <div class="bolt-heading-sm !font-medium">&reg;</div>
                <div class="bolt-heading-lg bolt-serif">&nbsp;{{ productInfo.name }}</div>
              </span>
            </h2>
            <h2 class="text-charcoal bolt-heading-xs !mb-8 text-center">
              Protection for your home systems and appliances
            </h2>
          </div>
          <div class="flex items-center" [ngClass]="'flex-col'">
            <div class="w-full max-w-none">
              <div class="mx-auto mt-8 w-[65%]">
                <div
                  class="bolt-container bg-secondary-container flex !h-[230px] w-full flex-col items-center justify-center p-6 text-center"
                  hscFsEventName="Cost Information Section Clicked"
                >
                  <div>
                    <p class="bolt-heading-xs !mt-2 !mb-0">Starting at</p>
                  </div>
                  <div>
                    <h1 class="bolt-heading-xl bolt-sans-serif">$85.00</h1>
                    <p class="bolt-body-md !mb-0">/mo + tax</p>
                  </div>
                  <div>
                    <p class="bolt-body-md !mb-2">+ $30 one-time enrollment fee</p>
                  </div>
                  <div>
                    <p class="bolt-body-md !mb-2">$150 service fee per claim</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="mt-8 flex w-full flex-col items-center justify-center">
              <h2 class="bolt-heading-sm !m-0 !p-0">What's covered?</h2>
              <div class="bolt-container mt-6">
                <div class="mb-6 grid grid-flow-col grid-rows-6 justify-center gap-x-8 lg:grid-rows-4 xl:grid-rows-3">
                  @for (item of coveredItems; track item) {
                    <div class="text-charcoal text-left text-nowrap">&bull; {{ item }}</div>
                  }
                </div>
              </div>
              <a
                class="bolt-body-md mb-6 py-1"
                href="#coverage-limits"
                hscFsEventName="Coverage Limits Link Clicked"
                (click)="scrollToSection($event, 'coverage-limits')"
                >View coverage limits</a
              >
              <hsc-sample-contract-modal />
            </div>
          </div>
        </div>
        <div class="flex items-stretch justify-center">
          @if (maintenanceModeEnabled()) {
            <hsc-maintenance-widget />
          } @else {
            <hsc-quoting-flow />
          }
        </div>
      </div>
    </div>
  `,
})
export class HeroWidget {
  private readonly launchDarklyService = inject(LaunchDarklyService);

  readonly coveredItems = COVERED_ITEMS;
  readonly productInfo = PRODUCT_INFO;
  scrollToSection = scrollToSection;

  readonly maintenanceModeEnabled: Signal<boolean> = this.launchDarklyService.getFlagSignal<boolean>(
    'maintenance-mode-enabled',
    false,
  );
}
